import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Upload,
  FileText,
  X,
  Library,
  Search,
  CheckCircle,
} from "lucide-react";
import {
  PatientDocument,
  documentTypes,
} from "@/lib/dashboard/patientDocumentsData";
import { ActionType } from "@/enum/ActionType";
import { useDocumentsData } from "@/components/Documents/documentsData";
import { useToast } from "@/hooks/use-toast";

interface LibraryDocument {
  id: string;
  title: string;
  type: string;
  folder?: string;
  sizeKB: number;
  mainFile:
    | {
        fileName: string;
      }
    | string;
}

interface UploadDocumentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isDarkMode: boolean;
  onUploadDocument?: (documentData: {
    file?: File;
    libraryDocId?: string;
    type: PatientDocument["type"];
    customName: string;
    title?: string;
    mainFile?: string;
  }) => void;
  isLoading?: boolean;
  trigger?: React.ReactNode;
}

export function UploadDocumentModal({
  open,
  onOpenChange,
  isDarkMode,
  onUploadDocument,
  isLoading = false,
  trigger,
}: UploadDocumentModalProps) {
  const [activeTab, setActiveTab] = React.useState<"upload" | "library">(
    "upload",
  );
  const [uploadedFile, setUploadedFile] = React.useState<File | null>(null);
  const [customFileName, setCustomFileName] = React.useState("");
  const [selectedDocumentType, setSelectedDocumentType] =
    React.useState<PatientDocument["type"]>("GP Letter");
  const [isDragActive, setIsDragActive] = React.useState(false);
  const [librarySearch, setLibrarySearch] = React.useState("");
  const [selectedLibraryDoc, setSelectedLibraryDoc] =
    React.useState<LibraryDocument | null>(null);
  const [fileError, setFileError] = React.useState<string>("");
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const {
    consultationId,
    updateConsultationActions,
    documentCompleted,
    setAddPatientAIChatMessage,
    SaveAIChatMessage,
    companyDocuments,
    documentSizeLimitInMbs = 10, // Default to 10MB if not provided
  } = useDocumentsData();

  // Helper function to validate file size
  const validateFileSize = (file: File): boolean => {
    const maxSizeInBytes = documentSizeLimitInMbs * 1024 * 1024;
    return file.size <= maxSizeInBytes;
  };

  // Helper function to format file size for display
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  // Validate file type
  const validateFileType = (file: File): boolean => {
    const validExtensions = [
      ".pdf",
      ".doc",
      ".docx",
      ".jpg",
      ".jpeg",
      ".png",
      ".txt",
    ];
    const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
    return validExtensions.includes(fileExtension);
  };

  // Get file validation error message
  const getFileValidationError = (file: File): string => {
    if (!validateFileType(file)) {
      return "Invalid file type. Please select a PDF, DOC, DOCX, JPG, PNG, or TXT file.";
    }

    if (!validateFileSize(file)) {
      return `File size (${formatFileSize(file.size)}) exceeds ${documentSizeLimitInMbs}MB limit.`;
    }

    return "";
  };

  const handleClose = () => {
    onOpenChange(false);
    setUploadedFile(null);
    setCustomFileName("");
    setSelectedDocumentType("GP Letter");
    setLibrarySearch("");
    setSelectedLibraryDoc(null);
    setActiveTab("upload");
    setFileError("");
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileSelect = (file: File) => {
    // Clear previous errors
    setFileError("");

    const error = getFileValidationError(file);
    if (error) {
      toast({
        title: "File Validation Error",
        description: error,
        variant: "destructive",
      });
      return;
    }

    setUploadedFile(file);
    setCustomFileName(file.name.replace(/\.[^/.]+$/, ""));
  };

  const filteredDocuments = companyDocuments?.filter(
    (doc) =>
      doc.title.toLowerCase().includes(librarySearch.toLowerCase()) ||
      doc.type.toLowerCase().includes(librarySearch.toLowerCase()) ||
      doc.folder?.toLowerCase().includes(librarySearch.toLowerCase()),
  );

  const handleLibraryDocSelect = (doc: LibraryDocument) => {
    setSelectedLibraryDoc(doc);
  };

  const handleUploadDocument = () => {
    if (activeTab === "upload") {
      if (!uploadedFile) {
        toast({
          title: "File Required",
          description: "Please select a file to upload.",
          variant: "destructive",
        });
        return;
      }

      if (!customFileName.trim()) {
        toast({
          title: "Document Name Required",
          description: "Please enter a document name.",
          variant: "destructive",
        });
        return;
      }

      // Final validation
      const error = getFileValidationError(uploadedFile);
      if (error) {
        toast({
          title: "File Validation Error",
          description: error,
          variant: "destructive",
        });
        return;
      }

      if (onUploadDocument) {
        const payload = {
          file: uploadedFile,
          type: selectedDocumentType,
          customName: customFileName.trim(),
        };
        onUploadDocument(payload);
      }
      handleClose();
    } else if (activeTab === "library" && selectedLibraryDoc) {
      if (onUploadDocument) {
        const payload = {
          libraryDocId: selectedLibraryDoc.id,
          type: selectedDocumentType,
          title: selectedLibraryDoc.title,
          mainFile: selectedLibraryDoc.mainFile.fileName,
        };

        onUploadDocument(payload);
      }
      handleClose();
    }
  };

  const canUpload =
    (activeTab === "upload" && uploadedFile && customFileName.trim()) ||
    (activeTab === "library" && selectedLibraryDoc);

  // Check if file is valid for upload button
  const isFileValidForUpload =
    activeTab === "upload"
      ? uploadedFile &&
        customFileName.trim() &&
        validateFileSize(uploadedFile) &&
        validateFileType(uploadedFile)
      : activeTab === "library" && selectedLibraryDoc;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger && <DialogContent asChild>{trigger}</DialogContent>}
      <DialogContent
        className={`max-w-2xl flex flex-col ${
          isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900"
        }`}
      >
        <DialogHeader>
          <DialogTitle
            className={`flex items-center gap-2 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            <Upload className="w-5 h-5" />
            Upload Document
          </DialogTitle>
        </DialogHeader>

        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as "upload" | "library")}
          className="flex flex-col flex-1 w-full min-h-0"
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="upload" className="flex items-center gap-2">
              <Upload className="w-4 h-4" />
              Upload File
            </TabsTrigger>
            <TabsTrigger value="library" className="flex items-center gap-2">
              <Library className="w-4 h-4" />
              Select from Library
            </TabsTrigger>
          </TabsList>

          <TabsContent
            value="upload"
            className="flex-1 py-4 pr-2 space-y-6 overflow-y-auto"
          >
            {/* File Upload Area */}
            <div className="space-y-2">
              <Label
                className={`${isDarkMode ? "text-gray-200" : "text-gray-700"}`}
              >
                Document
              </Label>
              {!uploadedFile ? (
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    isDragActive
                      ? "border-blue-500 bg-blue-50"
                      : isDarkMode
                        ? "border-gray-600 bg-gray-700/50"
                        : "border-gray-300 hover:border-gray-400 bg-gray-50"
                  }`}
                >
                  <Upload
                    className={`w-12 h-12 mx-auto mb-4 ${
                      isDarkMode ? "text-gray-400" : "text-gray-400"
                    }`}
                  />
                  <p
                    className={`text-sm mb-2 ${
                      isDarkMode ? "text-gray-200" : "text-gray-600"
                    }`}
                  >
                    Drag and drop a file here, or click to select
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    id="document-upload"
                    className="hidden"
                    onChange={(e) => {
                      const files = e.target.files;
                      if (files && files.length > 0) {
                        handleFileSelect(files[0]);
                      }
                    }}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      document.getElementById("document-upload")?.click()
                    }
                    className={
                      isDarkMode
                        ? "bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                        : ""
                    }
                  >
                    Select File
                  </Button>
                  <p
                    className={`text-xs mt-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Supported: PDF, DOC, DOCX, JPG, PNG, TXT (Max{" "}
                    {documentSizeLimitInMbs}MB)
                  </p>
                </div>
              ) : (
                <div
                  className={`border rounded-lg p-4 flex items-center justify-between ${
                    isDarkMode
                      ? "bg-gray-700 border-gray-600"
                      : "bg-gray-50 border-gray-200"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-8 h-8 text-blue-600" />
                    <div>
                      <p
                        className={`font-medium text-sm ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {uploadedFile.name}
                      </p>
                      <p
                        className={`text-xs ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        {formatFileSize(uploadedFile.size)}
                      </p>
                      {!validateFileSize(uploadedFile) && (
                        <div className="p-1 mt-1 border border-red-200 rounded-md bg-red-50">
                          <p className="flex items-center gap-1 text-xs text-red-600">
                            <span className="text-red-500">⚠</span>
                            File exceeds {documentSizeLimitInMbs}MB limit
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setUploadedFile(null);
                      setFileError("");
                      if (fileInputRef.current) {
                        fileInputRef.current.value = "";
                      }
                    }}
                    className={
                      isDarkMode ? "text-gray-400 hover:text-white" : ""
                    }
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>

            {/* Document Type Selection */}
            {uploadedFile && (
              <div className="space-y-2">
                <Label
                  className={`${
                    isDarkMode ? "text-gray-200" : "text-gray-700"
                  }`}
                >
                  Document Type
                </Label>
                <div
                  className={`border rounded-lg p-2 ${
                    isDarkMode
                      ? "bg-gray-700 border-gray-600"
                      : "bg-white border-gray-300"
                  }`}
                >
                  {documentTypes?.map((type) => (
                    <button
                      key={type}
                      onClick={() => setSelectedDocumentType(type)}
                      className={`w-full text-left px-3 py-2 text-sm rounded transition-colors ${
                        selectedDocumentType === type
                          ? isDarkMode
                            ? "bg-blue-600 text-white"
                            : "bg-blue-50 text-blue-900"
                          : isDarkMode
                            ? "hover:bg-gray-600 text-gray-100"
                            : "hover:bg-gray-100 text-gray-900"
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Document Name */}
            {uploadedFile && (
              <div className="space-y-2">
                <Label
                  className={`${
                    isDarkMode ? "text-gray-200" : "text-gray-700"
                  }`}
                >
                  Document Name
                </Label>
                <Input
                  value={customFileName}
                  onChange={(e) => setCustomFileName(e.target.value)}
                  placeholder="Enter document name"
                  className={`${
                    isDarkMode
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300"
                  }`}
                />
                {!customFileName.trim() && (
                  <div className="p-1 mt-1 border border-red-200 rounded-md bg-red-50">
                    <p className="flex items-center gap-1 text-xs text-red-600">
                      <span className="text-red-500">⚠</span>
                      Please enter a document name
                    </p>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent
            value="library"
            className="flex-1 py-4 pr-2 space-y-6 overflow-y-auto"
          >
            {/* Library Selection Area */}
            <div className="space-y-2">
              <Label
                className={`${isDarkMode ? "text-gray-200" : "text-gray-700"}`}
              >
                Select Document from Library
              </Label>

              {/* Search */}
              <div className="relative">
                <Search className="absolute w-4 h-4 text-gray-400 transform -translate-y-1/2 left-3 top-1/2" />
                <Input
                  placeholder="Search documents..."
                  value={librarySearch}
                  onChange={(e) => setLibrarySearch(e.target.value)}
                  className={`pl-10 ${
                    isDarkMode
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300"
                  }`}
                />
              </div>

              {/* Documents List */}
              <div
                className={`border rounded-lg max-h-64 overflow-y-auto ${
                  isDarkMode ? "border-gray-600 bg-gray-700" : "border-gray-300"
                }`}
              >
                {filteredDocuments?.length === 0 ? (
                  <div
                    className={`p-8 text-center ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    <FileText
                      className={`w-12 h-12 mx-auto mb-2 ${
                        isDarkMode ? "text-gray-500" : "text-gray-300"
                      }`}
                    />
                    <p className="text-sm">No documents found</p>
                  </div>
                ) : (
                  <div
                    className={
                      isDarkMode ? "divide-y divide-gray-600" : "divide-y"
                    }
                  >
                    {filteredDocuments?.map((doc) => (
                      <button
                        key={doc.id}
                        onClick={() => handleLibraryDocSelect(doc)}
                        className={`w-full p-3 text-left hover:bg-opacity-50 transition-colors flex items-start gap-3 ${
                          selectedLibraryDoc?.id === doc.id
                            ? isDarkMode
                              ? "bg-blue-900 border-l-4 border-blue-600"
                              : "bg-blue-50 border-l-4 border-blue-600"
                            : isDarkMode
                              ? "hover:bg-gray-600"
                              : "hover:bg-gray-50"
                        }`}
                      >
                        <FileText
                          className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                            selectedLibraryDoc?.id === doc.id
                              ? "text-blue-600"
                              : isDarkMode
                                ? "text-gray-400"
                                : "text-gray-400"
                          }`}
                        />
                        <div className="flex-1 min-w-0">
                          <p
                            className={`text-sm font-medium truncate ${
                              selectedLibraryDoc?.id === doc.id
                                ? isDarkMode
                                  ? "text-blue-300"
                                  : "text-blue-900"
                                : isDarkMode
                                  ? "text-gray-100"
                                  : "text-gray-900"
                            }`}
                          >
                            {doc.title}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span
                              className={`text-xs ${
                                isDarkMode ? "text-gray-400" : "text-gray-500"
                              }`}
                            >
                              {doc.type}
                            </span>
                            {doc.folder && (
                              <>
                                <span
                                  className={`text-xs ${
                                    isDarkMode
                                      ? "text-gray-500"
                                      : "text-gray-300"
                                  }`}
                                >
                                  •
                                </span>
                                <span
                                  className={`text-xs ${
                                    isDarkMode
                                      ? "text-gray-400"
                                      : "text-gray-500"
                                  }`}
                                >
                                  {doc.folder}
                                </span>
                              </>
                            )}
                            <span
                              className={`text-xs ${
                                isDarkMode ? "text-gray-500" : "text-gray-300"
                              }`}
                            >
                              •
                            </span>
                            <span
                              className={`text-xs ${
                                isDarkMode ? "text-gray-400" : "text-gray-500"
                              }`}
                            >
                              {formatFileSize(doc.sizeKB * 1024)}
                            </span>
                          </div>
                        </div>
                        {selectedLibraryDoc?.id === doc.id && (
                          <CheckCircle className="flex-shrink-0 w-5 h-5 text-blue-600" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Selected Document Preview */}
              {selectedLibraryDoc && (
                <div
                  className={`border rounded-lg p-4 ${
                    isDarkMode
                      ? "bg-blue-900/30 border-blue-600"
                      : "bg-blue-50 border-blue-200"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <CheckCircle className="flex-shrink-0 w-5 h-5 text-blue-600" />
                    <div className="flex-1">
                      <p
                        className={`text-sm font-medium ${
                          isDarkMode ? "text-blue-300" : "text-blue-900"
                        }`}
                      >
                        Selected: {selectedLibraryDoc.title}
                      </p>
                      <p
                        className={`text-xs mt-1 ${
                          isDarkMode ? "text-blue-400" : "text-blue-700"
                        }`}
                      >
                        {selectedLibraryDoc.type} •{" "}
                        {formatFileSize(selectedLibraryDoc.sizeKB * 1024)}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedLibraryDoc(null)}
                      className={
                        isDarkMode
                          ? "text-blue-400 hover:text-blue-300"
                          : "text-blue-600 hover:text-blue-700"
                      }
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="pt-4 mt-auto border-t bg-inherit">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
            className={isDarkMode ? "border-gray-600 text-gray-100" : ""}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUploadDocument}
            disabled={!isFileValidForUpload || isLoading}
            className="text-white bg-black hover:bg-gray-900"
          >
            {isLoading ? (
              <>
                <Upload className="w-4 h-4 mr-2 " />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Upload Document
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
